import { useCallback, useRef } from 'react';
import { clamp } from '../utils/clamp';
// Window used to compute release velocity from the recent drag trajectory.
// Single-event sampling misreads touch swipes because the last `touchmove`
// before `touchend` often has a near-zero delta (finger decelerating/lifting).
const VELOCITY_WINDOW_MS = 80;
const VELOCITY_MULTIPLIER = 10;
const VELOCITY_CAP = 60;
const ANIMATION_DURATION_MS = 250;
const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3);
export function useInertiaDrag({ minOffset, maxOffset, velocityThreshold, retention, setOffset }) {
    const draggingRef = useRef(false);
    const velocityRef = useRef(0);
    const lastPositionRef = useRef(0);
    const samplesRef = useRef([]);
    const animationRef = useRef(undefined);
    // True while a programmatic `animateTo` is in flight (external value sync).
    // Distinguished from inertia so `useValue` can suppress the spurious
    // intermediate `onChange` feedback that would otherwise overwrite the
    // caller's authoritative `value` with mid-animation offsets.
    const isAnimatingExternallyRef = useRef(false);
    const shutAnimation = () => {
        if (animationRef.current)
            cancelAnimationFrame(animationRef.current);
        animationRef.current = undefined;
        isAnimatingExternallyRef.current = false;
    };
    const startDrag = (newX) => {
        draggingRef.current = true;
        if (animationRef.current)
            shutAnimation();
        lastPositionRef.current = newX;
        samplesRef.current = [{ position: newX, time: performance.now() }];
    };
    const moveDrag = (newX) => {
        if (!draggingRef.current)
            return;
        const now = performance.now();
        const deltaX = newX - lastPositionRef.current;
        moveOffsetBy(deltaX);
        samplesRef.current.push({ position: newX, time: now });
        const cutoff = now - VELOCITY_WINDOW_MS;
        while (samplesRef.current.length > 2 && samplesRef.current[0].time < cutoff) {
            samplesRef.current.shift();
        }
        lastPositionRef.current = newX;
    };
    const endDrag = () => {
        draggingRef.current = false;
        velocityRef.current = computeReleaseVelocity();
        samplesRef.current = [];
        moveStep();
    };
    const computeReleaseVelocity = () => {
        // Trim samples relative to release time, not last moveDrag time. If the pointer
        // paused before release, the buffer holds stale samples from the earlier motion;
        // using them would launch inertia from a standstill.
        const cutoff = performance.now() - VELOCITY_WINDOW_MS;
        const recentSamples = samplesRef.current.filter((sample) => sample.time >= cutoff);
        if (recentSamples.length < 2)
            return 0;
        const firstSample = recentSamples[0];
        const lastSample = recentSamples[recentSamples.length - 1];
        const deltaTime = lastSample.time - firstSample.time;
        if (deltaTime <= 0)
            return 0;
        const deltaPosition = lastSample.position - firstSample.position;
        const capped = clamp((deltaPosition / deltaTime) * VELOCITY_MULTIPLIER, -VELOCITY_CAP, VELOCITY_CAP);
        if (Math.abs(capped) < velocityThreshold)
            return 0;
        return capped;
    };
    const moveStep = () => {
        velocityRef.current *= retention;
        if (Math.abs(velocityRef.current) < velocityThreshold)
            return shutAnimation();
        moveOffsetBy(velocityRef.current);
        animationRef.current = requestAnimationFrame(moveStep);
    };
    const moveOffsetBy = (moveBy) => {
        setOffset((prev) => {
            const next = prev + moveBy;
            if (animationRef.current && (next < maxOffset || next > minOffset)) {
                shutAnimation();
            }
            return clamp(next, maxOffset, minOffset);
        });
    };
    // Animates offset from `fromOffset` to `toOffset` over ANIMATION_DURATION_MS.
    // Reuses the same animationRef as inertia so the two cannot run simultaneously.
    // useCallback keeps the reference stable so useValue can depend on it without re-firing.
    //
    // `isExternal` marks an animation driven by a `value` prop change (so `useValue`
    // suppresses the intermediate `onChange` feedback). Tap-to-scroll passes `false`
    // so the offset→value sync emits `onChange` while sliding, exactly like inertia.
    const animateTo = useCallback((fromOffset, toOffset, isExternal = true) => {
        if (animationRef.current)
            cancelAnimationFrame(animationRef.current);
        isAnimatingExternallyRef.current = isExternal;
        const startTime = performance.now();
        const distance = toOffset - fromOffset;
        const step = () => {
            const elapsed = performance.now() - startTime;
            const progress = Math.min(1, elapsed / ANIMATION_DURATION_MS);
            const eased = easeOutCubic(progress);
            setOffset(fromOffset + distance * eased);
            if (progress < 1) {
                animationRef.current = requestAnimationFrame(step);
            }
            else {
                animationRef.current = undefined;
                isAnimatingExternallyRef.current = false;
            }
        };
        animationRef.current = requestAnimationFrame(step);
    }, [setOffset]);
    return {
        startDrag,
        moveDrag,
        endDrag,
        animateTo,
        movingLasts: animationRef.current !== undefined || draggingRef.current === true,
        isAnimatingExternally: isAnimatingExternallyRef.current,
    };
}
